import { useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import type { Biometric } from "@/lib/api_client";
import { useUpdateBiometric } from "@/hooks/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { HtmlPreview } from "@/components/shared/html-preview";

interface TextsTabProps {
  biometric: Biometric;
}

const TEXT_FIELDS = [
  { key: "about" as const, label: "Beskrivelse" },
  { key: "howToIncrease" as const, label: "Hvordan oke" },
  { key: "howToDecrease" as const, label: "Hvordan redusere" },
];

export function TextsTab({ biometric }: TextsTabProps) {
  const updateBiometric = useUpdateBiometric();
  const [texts, setTexts] = useState({
    about: biometric.about ?? "",
    howToIncrease: biometric.howToIncrease ?? "",
    howToDecrease: biometric.howToDecrease ?? "",
  });

  const handleSave = async () => {
    await updateBiometric.mutateAsync({
      id: biometric.id,
      data: {
        about: texts.about || null,
        howToIncrease: texts.howToIncrease || null,
        howToDecrease: texts.howToDecrease || null,
      },
    });
    toast.success("Tekster lagret");
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="about">
        <TabsList>
          {TEXT_FIELDS.map((f) => (
            <TabsTrigger key={f.key} value={f.key}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TEXT_FIELDS.map((f) => (
          <TabsContent key={f.key} value={f.key}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-admin-text-secondary mb-2">
                  HTML-kilde
                </label>
                <Textarea
                  value={texts[f.key]}
                  onChange={(e) => setTexts((t) => ({ ...t, [f.key]: e.target.value }))}
                  className="min-h-[300px]"
                  placeholder={`Skriv HTML for ${f.label.toLowerCase()}...`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin-text-secondary mb-2">
                  Forhandsvisning
                </label>
                <HtmlPreview
                  html={texts[f.key] || "<p style='color:#999'>Ingen innhold</p>"}
                  className="min-h-[300px] border border-admin-card-border"
                />
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Button onClick={handleSave} disabled={updateBiometric.isPending}>
        <Save className="h-4 w-4" />
        Lagre tekster
      </Button>
    </div>
  );
}
